/**
 * MentalityPanel — the full aside panel rendered when the FAB is open.
 *
 * Composes:
 *   - Header (eyebrow, title, close)
 *   - Module selector
 *   - Loading / error / body (suggested cards + chat thread)
 *   - Composer (owned by MentalityComposer)
 */

import { useMemo, useState } from 'react';
import {
    Sparkles, X, ChevronDown, AlertTriangle, RefreshCw, Lightbulb,
} from 'lucide-react';
import Button from '../../../core/ui/Button';
import {
    MODULE_LABELS,
    MODULE_ORDER,
    type ModuleKey,
} from '../../../mentorshipService';
import { useMentalityContext } from './useMentalityContext';
import { useMentalityChat } from './useMentalityChat';
import { useMentalityDocument } from './useMentalityDocument';
import { SuggestedCards, ChatBubble } from './Cards';
import { SkeletonOverlay } from '../../../core/ui/skeleton';
import MentalityComposer from './MentalityComposer';
import { riskToCard, insightToCard, profileToCards, type MatchCard } from './adapters';

type Props = {
    open: boolean;
    onClose: () => void;
};

export default function MentalityPanel({ open, onClose }: Props) {
    const [module, setModule] = useState<ModuleKey | ''>('');
    const [query, setQuery] = useState('');

    const { context, loading, error, fetchContext, cancelRef } =
        useMentalityContext(open, module);

    const {
        docName, docText, uploading, uploadTruncated, uploadError,
        upload, clear, dismissError,
    } = useMentalityDocument({ open, cancelRef });

    const { chat, sending, send } = useMentalityChat({
        open, context, module, docName, docText, cancelRef,
    });

    const allCards = useMemo<MatchCard[]>(() => {
        if (!context) return [];
        return [
            ...context.risks.map(riskToCard),
            ...context.insights.map(insightToCard),
            ...context.client_profiles.flatMap(profileToCards),
        ];
    }, [context]);

    const hasAnyContent = allCards.length > 0;

    const handleSend = () => {
        void send(query).then(() => setQuery(''));
    };

    return (
        <aside className="mentality-overlay" role="dialog" aria-label="Mentality overlay">
            <header className="mentality-overlay__head">
                <div className="mentality-overlay__title-group">
                    <span className="mentality-overlay__eyebrow">
                        <Sparkles size={12} aria-hidden="true" /> Mentality overlay
                    </span>
                    <h2 className="mentality-overlay__title">Situational guidance</h2>
                </div>
                <button
                    type="button"
                    className="mentality-overlay__close"
                    onClick={onClose}
                    aria-label="Close overlay"
                >
                    <X size={16} />
                </button>
            </header>

            <label className="mentality-overlay__module">
                <span className="mentality-overlay__module-label">Working on</span>
                <div className="mentality-overlay__module-select">
                    <select
                        value={module}
                        onChange={(e) => setModule(e.target.value as ModuleKey | '')}
                    >
                        <option value="">Any document</option>
                        {MODULE_ORDER.map((m) => (
                            <option key={m} value={m}>{MODULE_LABELS[m]}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} aria-hidden="true" />
                </div>
            </label>

            {loading && <SkeletonOverlay lines={4} />}

            {!loading && error && (
                <div className="mentality-overlay__error" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    {error}
                    <Button
                        variant="ghost"
                        size="sm"
                        icon={<RefreshCw size={12} />}
                        onClick={() => void fetchContext()}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {!loading && !error && context && (
                <div className="mentality-overlay__body">
                    {!hasAnyContent ? (
                        <div className="mentality-overlay__empty">
                            <Lightbulb size={20} />
                            <p>
                                Your mentor hasn't curated any risks or insights
                                for this context yet. Check back once your next
                                review lands.
                            </p>
                        </div>
                    ) : (
                        <>
                            <SuggestedCards cards={allCards.slice(0, 4)} />
                            {chat.length > 0 && (
                                <div className="mentality-overlay__chat">
                                    {chat.map((entry) => (
                                        <ChatBubble key={entry.id} entry={entry} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <footer className="mentality-overlay__foot">
                <MentalityComposer
                    query={query}
                    onQueryChange={setQuery}
                    onSend={handleSend}
                    sending={sending}
                    hasAnyContent={hasAnyContent}
                    docName={docName}
                    docText={docText}
                    uploading={uploading}
                    uploadTruncated={uploadTruncated}
                    uploadError={uploadError}
                    onUpload={upload}
                    onClearDoc={clear}
                    onDismissError={dismissError}
                />
            </footer>
        </aside>
    );
}
